import { useId, useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { ChevronDown, Check } from "lucide-react";

interface SelectOption {
  value: string;
  label: string;
}

interface SelectFieldProps {
  label: string;
  /** value peut être null (ex. colonne DB nullable) — on le coerce en "" en interne */
  value?: string | null;
  options: SelectOption[] | null | undefined;
  onChange?: (value: string) => void;
  error?: string;
  placeholder?: string;
  className?: string;
  highlight?: boolean;
  /** Hint visuel pour la validation EN PRÉPARATION (cf. lib/bienPrepValidation.ts). */
  missingHint?: "required" | "mailing";
  /**
   * Rend le label À GAUCHE du contrôle au lieu de l'empiler au-dessus.
   * Pour les en-têtes compacts qui doivent tenir sur une seule ligne
   * (cf. FicheHeader). Le panneau déroulant reste ancré sous le bouton.
   */
  inlineLabel?: boolean;
  /**
   * Rend le libellé SOUS le contrôle, en petit et centré — pour les champs
   * réunis dans un FieldGroup (« Parking » : disponible · n° · fermé).
   */
  labelBelow?: boolean;
  /**
   * Champ VERROUILLÉ : même boîte, même padding, même place dans la grille
   * qu'un champ éditable — mais inerte. Aucun repère visuel supplémentaire
   * (décision Thibaud 2026-08-09) : seul le survol distingue les deux, un champ
   * éditable prenant une bordure pointillée.
   *
   * Même contrat que `FormField.readOnly`, pour que le helper `F()` des fiches
   * puisse rendre LE MÊME composant au lieu de substituer un `ReadOnlyRow` —
   * c'est cette substitution qui produisait un damier de deux formes de ligne
   * dès qu'un bloc mélangeait champs éditables et verrouillés.
   */
  readOnly?: boolean;
  /** Alignement de la valeur dans le bouton (le chevron reste a droite). */
  align?: "left" | "center";
  /** Libellé en gras au lieu du poids medium habituel — mise en avant ponctuelle
   *  (ex. « Statut bien » dans FicheHeader). Pas un défaut : `cn()` ne résout pas
   *  les conflits de classes, une classe externe ne peut pas écraser celle-ci. */
  boldLabel?: boolean;
  /** Fond du bouton — remplace `bg-surface-2` par défaut (même raison que
   *  `boldLabel`) pour un champ ponctuellement mis en avant. */
  bgClassName?: string;
}

export function SelectField({
  label,
  value,
  options,
  onChange,
  error,
  placeholder = "...",
  className,
  highlight: _highlight, // accepté pour compat API, ignoré (cf. note ci-dessous)
  missingHint,
  inlineLabel = false,
  labelBelow = false,
  align = "left",
  readOnly = false,
  boldLabel = false,
  bgClassName = "bg-surface-2",
}: SelectFieldProps) {
  void _highlight;
  const labelWeightClass = boldLabel ? "font-bold" : "font-medium";
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerId = useId();

  // Défense : une colonne DB nullable ou une option array non chargée ne doit
  // pas crasher le composant. On coerce les cas null/undefined en valeurs safe.
  const safeValue = typeof value === "string" ? value : "";
  const safeOptions = Array.isArray(options) ? options : [];
  const isFilled = safeValue.length > 0;
  const selectedLabel = safeOptions.find((o) => o.value === safeValue)?.label;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const showMissingBorder = !readOnly && !isFilled && !isOpen && !error && !!missingHint;
  const missingBorderClass =
    missingHint === "required"
      ? "border border-solid border-danger"
      : missingHint === "mailing"
        ? "border border-solid border-warning"
        : "";

  const borderClasses = readOnly
    ? "border border-solid border-transparent"
    : error
    ? "border border-solid border-danger"
    : showMissingBorder
      ? missingBorderClass
      : isOpen
        ? "border border-solid border-border-focus"
        : isFilled && isHovered
          ? "border border-dashed border-border-hover"
          : isFilled
            ? "border border-solid border-transparent"
            : isHovered
              ? "border border-dashed border-border-hover"
              : "border border-dashed border-border";

  // On ne colore le label QUE si le champ est vide ET tagué par le système de
  // validation EN PRÉPARATION/DISPONIBLE. Le prop highlight (c_level pré-existant)
  // n'affecte plus la couleur depuis qu'on a retiré sa légende.
  // Priorité : required > mailing > muted.
  const labelColorClass = isFilled
    ? "text-text-muted"
    : missingHint === "required"
      ? "text-danger"
      : missingHint === "mailing"
        ? "text-warning"
        : "text-text-muted";

  // Bouton + panneau déroulant + erreur. Le panneau est positionné en absolu
  // dans un conteneur `relative` FRÈRE du bouton : il doit donc rester dans une
  // colonne avec lui, y compris en mode `inlineLabel` (sinon il devient un
  // 3e élément de la ligne et s'affiche à côté du bouton, pas dessous).
  const control = (
    <>
      <button
        id={triggerId}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={cn(
          "px-3 py-2 rounded-md text-sm text-left",
          bgClassName,
          "outline-none shadow-none transition-colors duration-150",
          "flex items-center justify-between gap-2",
          // `cn` est un simple clsx : deux classes `cursor-*` cohabiteraient
          // sans que l'ordre décide. On tranche ici, à la source.
          readOnly ? "cursor-default" : "cursor-pointer",
          borderClasses,
          isFilled ? "text-text-primary" : "text-text-muted"
        )}
        disabled={readOnly}
        tabIndex={readOnly ? -1 : undefined}
        onClick={() => !readOnly && setIsOpen(!isOpen)}
        onMouseEnter={() => !readOnly && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span className={cn("min-w-0 truncate", align === "center" && "flex-1 text-center")}>
          {selectedLabel || (readOnly ? "—" : placeholder)}
        </span>
        {/* Le chevron promet un menu : on le retire quand il n'y en a pas.
            La place reste occupée pour que la valeur ne se recentre pas. */}
        {readOnly ? (
          <span className="w-4 shrink-0" aria-hidden />
        ) : (
          <ChevronDown
            size={16}
            className={cn(
              "text-text-muted transition-transform duration-150",
              isOpen && "rotate-180"
            )}
          />
        )}
      </button>
      {isOpen && !readOnly && (
        <div className="relative z-10">
          <div className="absolute top-1 left-0 right-0 bg-surface border border-border rounded-md shadow-lg max-h-[240px] overflow-y-auto">
            {safeOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={cn(
                  "w-full px-3 py-2 text-sm text-left flex items-center justify-between",
                  "hover:bg-gray-200 transition-colors cursor-pointer"
                )}
                onClick={() => {
                  onChange?.(option.value);
                  setIsOpen(false);
                }}
              >
                <span>{option.label}</span>
                {option.value === safeValue && (
                  <Check size={16} className="text-accent" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
      {error && (
        <span className="text-xs text-danger mt-1 px-3">{error}</span>
      )}
    </>
  );

  if (labelBelow) {
    return (
      <div className={cn("flex flex-col", className)} ref={ref}>
        {control}
        <label htmlFor={triggerId} className={cn("text-[9.5px] tracking-wider uppercase text-center truncate mt-1 px-0.5", labelWeightClass, labelColorClass)}>
          {label}
        </label>
      </div>
    );
  }

  if (inlineLabel) {
    return (
      <div className={cn("flex flex-row items-center gap-3", className)} ref={ref}>
        <label htmlFor={triggerId} className={cn("text-xs tracking-wider uppercase whitespace-nowrap", labelWeightClass, labelColorClass)}>
          {label}
        </label>
        {/* Largeur MINIMALE, et non `min-w-0`.
            En mode inlineLabel le composant vit dans une bande d'en-tête en
            `flex` : sans base, il se réduisait à son contenu. Une valeur courte
            — ou vide, donc « ... » — donnait un bouton de 92 px, et comme le
            panneau déroulant est positionné `left-0 right-0` sur ce bouton, les
            options y passaient à la ligne (« En prépara… »).
            180 px couvrent le pire cas mesuré : « EN PRÉPARATION » en capitales
            sur la fiche location demande 167 px, chevron et padding compris. */}
        <div className="flex flex-col flex-1 min-w-[180px]">{control}</div>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col", className)} ref={ref}>
      <label htmlFor={triggerId} className={cn("text-xs tracking-wider uppercase px-3 mb-1", labelWeightClass, labelColorClass)}>
        {label}
      </label>
      {control}
    </div>
  );
}
